// Shared pieces for every work surface (the per-client tab, the fleet-wide
// dashboard, the meetings tab, the link popover), so a status chip or a row
// cannot mean one thing in one view and something else in another.

import { Box, Chip, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SOURCE_COLORS, SOURCES } from '../../services/workService';
import { Tip, Explain } from './explain';
import X from './explainText';
import { personName, usePeopleNames } from '../../services/peopleNames';
import { useOwnerReach } from '../../services/ownerReach';
import { useAccessSignals } from '../../services/accessSignals';
import { useClientOwner } from '../../services/clientOwners';
import { useEmailReader } from './EmailReader';

export const BLUE = '#004df2';
export const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.key, s.label]));

/** Where each source's items actually live, stated plainly in the UI. */
export const SOURCE_ORIGIN = {
    ado: 'Azure DevOps · Nexus project, excluding Tasks',
    monday: 'Monday · Technical Outreach Requests board',
    todo: 'Client Overview to-dos',
    request: 'Implementations · success requests',
    email: 'Client Overview · email threads from every Nexus mailbox, with a message from the district',
};

/**
 * ADO publishes a state CATEGORY (Proposed/InProgress/Resolved/Completed)
 * alongside the state name, and the category is what decides whether work is
 * open. This project has a state called "Done This Week" whose category is
 * Resolved — reading the name would count finished work as backlog.
 */
export const CATEGORY_TONE = {
    Proposed: { label: 'Not started', fg: '#6b7280', bg: 'rgba(107,114,128,.14)' },
    InProgress: { label: 'In progress', fg: '#1d4ed8', bg: 'rgba(29,78,216,.14)' },
    Resolved: { label: 'Resolved', fg: '#047857', bg: 'rgba(4,120,87,.14)' },
    Completed: { label: 'Closed', fg: '#4b5563', bg: 'rgba(75,85,99,.14)' },
    Removed: { label: 'Removed', fg: '#b45309', bg: 'rgba(180,83,9,.14)' },
};

export const PRIORITY_TONE = {
    Critical: '#d4102e', Urgent: '#d4102e', High: '#f0556d',
    Medium: '#f0a93b', Low: '#28c281',
};

export const chartColors = (mode) => SOURCE_COLORS[mode === 'dark' ? 'dark' : 'light'];

export function SourceDot({ source, mode, size = 9 }) {
    return (
        <Tip title={SOURCE_ORIGIN[source] || source}
            explain={X.sourceDot(SOURCE_LABEL[source] || source, SOURCE_ORIGIN[source] || source)}>
            <Box component="span" aria-hidden sx={{
                width: size, height: size, borderRadius: '50%', flexShrink: 0,
                display: 'inline-block', verticalAlign: 'middle',
                background: chartColors(mode)[source] || '#888',
            }} />
        </Tip>
    );
}

/** Identity is never colour alone — the dot always travels with its label. */
export function SourceTag({ source, mode }) {
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <SourceDot source={source} mode={mode} />
            <Typography component="span" sx={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                {SOURCE_LABEL[source] || source}
            </Typography>
        </Box>
    );
}

export function StatusChip({ item }) {
    const tone = CATEGORY_TONE[item.state_category] || CATEGORY_TONE.InProgress;
    // The raw state is what the source system shows; the category is our
    // normalisation. Both are visible so neither has to be trusted blindly.
    return (
        <Tip title={`${tone.label} — ${item.status || 'no state'}`} explain={X.statusChip(item)}>
            <Chip label={item.status || tone.label} size="small" sx={{
                height: 20, fontSize: 11, fontWeight: 600,
                color: tone.fg, bgcolor: tone.bg, borderRadius: '4px',
            }} />
        </Tip>
    );
}

export function PriorityChip({ value }) {
    if (!value) return null;
    const c = PRIORITY_TONE[value] || '#6b7280';
    return (
        <Tip explain={X.priorityChip(value)}>
            <Chip label={value} size="small" variant="outlined" sx={{
                height: 20, fontSize: 11, color: c, borderColor: c, borderRadius: '4px',
            }} />
        </Tip>
    );
}

/**
 * Wraps a listed work item in a real link to wherever it actually lives.
 *
 * A real anchor, not an onClick that calls window.open: middle-click,
 * cmd-click, "open in new tab" and "copy link address" are how people
 * actually move around a list of tickets, and a handler silently breaks all
 * four. External items (ADO, Monday) open in a new tab so this page is not
 * lost; in-app ones (to-dos, requests) route without a reload.
 *
 * An item with no URL renders as plain content rather than a dead link that
 * looks clickable and is not.
 */
/** An email row's title opens the thread's reader right here, not Client Overview
 *  (Patrick, 2026-10-20). A plain button: there is no page to middle-click to. */
function EmailTitle({ item, base, children }) {
    const { open, loading, host } = useEmailReader(item);
    return (
        <>
            <Box component="button" type="button" onClick={open} title={loading ? 'Reading…' : 'Read the thread here'}
                aria-label="Read the thread here"
                sx={{ ...base, background: 'none', border: 0, padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%' }}>
                {children}
            </Box>
            {host}
        </>
    );
}

export function WorkItemLink({ item, children, sx = {} }) {
    const base = {
        display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none',
        color: 'inherit', ...sx,
    };
    if (!item.url) return <Box sx={base}>{children}</Box>;
    if (item.source === 'email') return <EmailTitle item={item} base={base}>{children}</EmailTitle>;
    const external = /^https?:/i.test(item.url);
    const label = `Open in ${SOURCE_ORIGIN[item.source]?.split(' · ')[0] || 'its own system'}`;
    return external ? (
        <Box component="a" href={item.url} target="_blank" rel="noreferrer"
            title={label} aria-label={label} sx={base}>
            {children}
        </Box>
    ) : (
        <Box component={RouterLink} to={item.url} title={label} aria-label={label} sx={base}>
            {children}
        </Box>
    );
}

export const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—'
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

export const daysSince = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

/** Monday-start week key, so "this week" means the same thing everywhere. */
// Local calendar date as YYYY-MM-DD. Deliberately NOT toISOString(), which
// converts to UTC first: local midnight west of Greenwich lands on the previous
// UTC day, so a Monday key would come back stamped Sunday.
const ymd = (d) => [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
].join('-');

/** The Monday on or before `date`, as a local YYYY-MM-DD key. */
export const mondayOf = (date) => {
    const monday = new Date(date);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    return ymd(monday);
};

/** Reads a YYYY-MM-DD key back as a local date, for labelling. */
export const fromWeekKey = (key) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export const weekKey = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return mondayOf(d);
};

// ---------------------------------------------------------------------------
// Movement — what makes this worth opening again tomorrow
// ---------------------------------------------------------------------------

const TERMINAL = new Set(['Resolved', 'Completed']);

/** Finished: someone did the work. */
export const isClosed = (i) => TERMINAL.has(i.state_category);

/**
 * Withdrawn: cancelled or deleted. A third outcome, not a flavour of done —
 * a cancelled request and a delivered dashboard are opposite facts, and
 * lumping them together inflates how much was actually finished.
 */
export const isWithdrawn = (i) => i.state_category === 'Removed';

/** Hours since an ISO timestamp, or null when it is missing/unparseable. */
export const hoursSince = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return (Date.now() - t) / 3.6e6;
};

const within = (iso, hours) => {
    const h = hoursSince(iso);
    return h !== null && h >= 0 && h <= hours;
};

/**
 * Raised / closed / moved inside a window, plus the net change in open work.
 *
 * "Closed" is an item now in a terminal state that ENTERED that state inside
 * the window — not merely one that is closed and was touched. "Moved" counts
 * every state change including those closures, so moved >= closed always.
 *
 * Net is raised − closed: what the open queue did. It is the number worth
 * checking, because the standing total barely shifts day to day while the flow
 * behind it does.
 */
export const flow = (items, hours) => {
    const raised = items.filter((i) => within(i.createdOn, hours)).length;
    const moved = items.filter((i) => within(i.movedOn, hours)).length;
    const closed = items.filter((i) => isClosed(i) && within(i.movedOn, hours)).length;
    return { raised, closed, moved, net: raised - closed };
};

/** Open items that have had no state change in `days` — the quiet rot. */
export const stalled = (items, days = 30) => items.filter(
    (i) => i.open && (hoursSince(i.movedOn) ?? 0) > days * 24,
);

export const AGE_BANDS = [
    { key: '0-7', label: '0–7d', max: 7 },
    { key: '8-30', label: '8–30d', max: 30 },
    { key: '31-90', label: '31–90d', max: 90 },
    { key: '90+', label: '90d+', max: Infinity },
];

export const ageBand = (i) => {
    const d = daysSince(i.createdOn);
    if (d === null) return '90+';
    return (AGE_BANDS.find((b) => d <= b.max) || AGE_BANDS[3]).key;
};

/**
 * The dimensions work can be sliced by. Each returns a display key; the
 * explorer groups on it, so adding a dimension here adds it to every menu.
 */
export const DIMENSIONS = [
    { key: 'client', label: 'Client', of: (i) => (i.attribution === 'resolved'
        ? `${i.clientTitle}${i.clientInactive ? ' (inactive)' : ''}`
        : 'Not one client') },
    { key: 'source', label: 'System', of: (i) => SOURCE_LABEL[i.source] || i.source },
    { key: 'type', label: 'Work type', of: (i) => i.type || 'Untyped' },
    { key: 'status', label: 'Status', of: (i) => i.status || 'No status' },
    { key: 'blocked', label: 'Blocked', of: (i) => (
        isBlocked(i) ? (blockedReason(i) || 'Blocked')
            : canExpressBlocked(i.source) ? 'Not blocked'
                : 'Cannot say — no blocked state') },
    { key: 'due', label: 'Deadline', of: (i) => {
        if (!canExpressDue(i.source)) return 'Cannot say — no deadline field';
        const d = daysUntilDue(i);
        if (d === null) return 'No date set';
        if (d < 0) return 'Overdue';
        if (d <= 7) return 'Due within 7 days';
        if (d <= 30) return 'Due within 30 days';
        return 'Due later';
    } },
    { key: 'priority', label: 'Priority', of: (i) => (
        !canExpressPriority(i.source) ? 'Cannot say — no priority field' : (i.priority || 'Unset')) },
    { key: 'owner', label: 'Owner', of: (i) => personName(i.owner) || 'Unassigned' },
    { key: 'requestor', label: 'Requestor', of: (i) => (
        !canExpressRequestor(i.source) ? 'Cannot say — no requestor field' : (personName(i.requestor) || 'Unset')) },
    { key: 'age', label: 'Age', of: (i) => (AGE_BANDS.find((b) => b.key === ageBand(i)) || {}).label },
    { key: 'week', label: 'Week raised', of: (i) => {
        const k = weekKey(i.createdOn);
        return k ? fromWeekKey(k).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Undated';
    } },
];

export const dimension = (key) => DIMENSIONS.find((d) => d.key === key) || DIMENSIONS[0];

// ---------------------------------------------------------------------------
// Blocked — work that cannot move, and the systems that cannot say so
// ---------------------------------------------------------------------------

/**
 * Which statuses mean "blocked", per system, and which systems have no way to
 * express it at all.
 *
 * Verified against each system's own schema rather than assumed:
 *   - Outreach (Monday) status options are Drafted / Sent / Awaiting client /
 *     Verify / Needs Follow-Up / Internally Investigating / BLOCKED / Closed.
 *   - Requests carry a Blocked status of their own.
 *   - DevOps has ELEVEN states (To do, Ready for Exploration, Exploration,
 *     Task Review, In Progress, PR Review, Data Review, Deployment, Final
 *     Review, Done This Week, Done) and not one of them is Blocked. There is
 *     no Blocked field on the work items either; it is a TAG.
 *   - To-dos have no blocked concept.
 *
 * `null` means "this system cannot say", which is NOT the same as "nothing is
 * blocked here" and must never be rendered as a zero.
 */
export const BLOCKED_BY_STATUS = {
    monday: ['blocked'],
    request: ['blocked'],
};

// DevOps marks blocked with a TAG, not a state — its eleven states are all
// stages of progress. 54 of 190 open items carry one.
const BLOCKED_TAG = /^blocked\b/i;

/** Systems with no way at all to record that work is stuck. An email thread
 *  says blocked with an Outlook tag starting "Waiting" (decided server-side). */
const BLOCKED_BLIND = new Set(['todo']);

export const canExpressBlocked = (source) => !BLOCKED_BLIND.has(source);

export const isBlocked = (i) => {
    if (BLOCKED_BLIND.has(i.source)) return false;
    if (i.source === 'email') return !!i.blocked;
    if ((i.tags || []).some((t) => BLOCKED_TAG.test(t))) return true;
    const vocab = BLOCKED_BY_STATUS[i.source];
    return !!vocab && vocab.includes(String(i.status || '').trim().toLowerCase());
};

/** The system's own words for why, so "Blocked-Initiate" is not flattened. */
export const blockedReason = (i) => (
    (i.tags || []).find((t) => BLOCKED_TAG.test(t)) || (isBlocked(i) ? i.status : '')
);

/** Systems that can report blocked work, and those that cannot. */
const coverage = (can) => ({
    can: SOURCES.filter((s) => can(s.key)).map((s) => s.label),
    cannot: SOURCES.filter((s) => !can(s.key)).map((s) => s.label),
});

export const blockedCoverage = () => coverage(canExpressBlocked);
export const dueCoverage = () => coverage(canExpressDue);

// ---------------------------------------------------------------------------
// Priority / requestor — fields a system may have no way to express at all.
// Same "cannot say, never a blank that reads as zero" rule as blocked/due
// above.
// ---------------------------------------------------------------------------

// Monday grew a Priority column and a People (stakeholders) column during
// Phase B. ADO's requestor and stakeholders live in analytics' own
// item_metadata store (no ADO type we file carries either field), written
// when the item is filed, so every source can express both.
// An email thread has no priority field at all (Outlook importance is the
// sender's, not ours, and is not read). Its requestor is the district sender.
const PRIORITY_BLIND = new Set(['email']);
const REQUESTOR_BLIND = new Set([]);

export const canExpressPriority = (source) => !PRIORITY_BLIND.has(source);
export const canExpressRequestor = (source) => !REQUESTOR_BLIND.has(source);

export const priorityCoverage = () => coverage(canExpressPriority);
export const requestorCoverage = () => coverage(canExpressRequestor);

export function BlockedChip({ item, days }) {
    if (!isBlocked(item)) return null;
    const reason = blockedReason(item);
    return (
        <Tip title={`${reason || 'Blocked'} — cannot move until something outside it changes`
            + (days > 0 ? `; unchanged for ${days} day${days === 1 ? '' : 's'}` : '')}
            explain={X.blockedChip(item, reason || 'Blocked', days)}>
            <Chip label={days > 0 ? `${reason} ${days}d` : reason || 'Blocked'} size="small" sx={{
                height: 20, fontSize: 11, fontWeight: 700, borderRadius: '4px',
                color: '#b91c1c', bgcolor: 'rgba(185,28,28,.16)',
            }} />
        </Tip>
    );
}

// ---------------------------------------------------------------------------
// Deadlines
// ---------------------------------------------------------------------------

/**
 * Every system carries a deadline now: DevOps has DueDate, Outreach has the
 * board's "Needed by", a to-do has its check-in date, and a request has
 * success.NexusSuccessRequest.DueDate (added with the work-view
 * standardisation; the views wrongly called Requests blind here until
 * 2026-09-22). The set is kept so the "cannot say" rule survives if a
 * source ever loses its field.
 */
// An email thread carries no deadline: "cannot say", never a blank that reads
// as "not due".
const DUE_BLIND = new Set(['email']);

export const canExpressDue = (source) => !DUE_BLIND.has(source);

/** Days until due — negative is overdue, null when there is no date. */
export const daysUntilDue = (i) => {
    if (!i.dueOn || DUE_BLIND.has(i.source)) return null;
    const d = new Date(i.dueOn);
    if (Number.isNaN(d.getTime())) return null;
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - midnight.getTime()) / 86400000);
};

export const isOverdue = (i) => {
    const d = daysUntilDue(i);
    return i.open && d !== null && d < 0;
};

export const isDueSoon = (i, within = 7) => {
    const d = daysUntilDue(i);
    return i.open && d !== null && d >= 0 && d <= within;
};

export function DueChip({ item }) {
    const d = daysUntilDue(item);
    if (d === null || !item.open) return null;
    if (d > 7) return null;
    const overdue = d < 0;
    const tone = overdue ? '#b91c1c' : '#b45309';
    // A to-do has no deadline; it has a CHECK-IN date, and the chip keeps
    // that word so the concept survives the shared row.
    const word = item.source === 'todo' ? 'check-in' : 'due';
    return (
        <Tip title={`${item.source === 'todo' ? 'Check-in' : 'Due'} ${fmtDate(item.dueOn)}`} explain={X.dueChip(item)}>
            <Chip
                label={overdue ? (item.source === 'todo' ? `check-in ${Math.abs(d)}d ago` : `${Math.abs(d)}d overdue`)
                    : d === 0 ? `${word} today` : `${word} in ${d}d`}
                size="small" variant="outlined"
                sx={{ height: 20, fontSize: 11, fontWeight: 600, borderRadius: '4px',
                    color: tone, borderColor: tone }}
            />
        </Tip>
    );
}

// ---------------------------------------------------------------------------
// Where a piece of work came from
// ---------------------------------------------------------------------------

/**
 * The meeting a work item came out of, if anyone has said so.
 *
 * Most work has no meeting, and that must read as "not linked", never as
 * "came from nowhere" — the same rule as the em-dashes on blocked and
 * overdue. Only a promise is coloured, because a promise made to a district
 * on a call is the one that turns into an apology when it slips.
 */
// How a work item relates to a meeting, in words: "Prep for Sep 24".
export const MEETING_RELATION_WORDS = {
    promised_at: 'Promised',
    raised_at: 'Raised',
    discussed_at: 'Discussed',
    prep_for: 'Prep for',
};
const meetingDay = (l) => (l?.meeting_start_utc
    ? new Date(`${String(l.meeting_start_utc).replace(/Z$/, '')}Z`).toLocaleDateString('en-US',
        { month: 'short', day: 'numeric' })
    : '');
export const meetingPhrase = (l) => `${MEETING_RELATION_WORDS[l?.relation] || 'Linked to'} ${meetingDay(l)}`.trim();

/** Which link the chip leads with: a promise (the one that turns into an
 *  apology when it slips), then the next meeting to prep for, then the most
 *  recent meeting. */
export const leadMeetingLink = (links, now = Date.now()) => {
    const at = (l) => (l.meeting_start_utc ? Date.parse(`${String(l.meeting_start_utc).replace(/Z$/, '')}Z`) : 0);
    const promise = links.find((l) => l.relation === 'promised_at');
    if (promise) return promise;
    const upcoming = links.filter((l) => l.relation === 'prep_for' && at(l) >= now - 86400000)
        .sort((a, b) => at(a) - at(b))[0];
    if (upcoming) return upcoming;
    return links.slice().sort((a, b) => at(b) - at(a))[0];
};

export function MeetingChip({ links }) {
    if (!links || !links.length) return null;
    const link = leadMeetingLink(links);
    const promise = link.relation === 'promised_at';
    const prep = link.relation === 'prep_for';
    const label = `${meetingPhrase(link)}${links.length > 1 ? ` +${links.length - 1}` : ''}`;
    const title = links.map((l) => `${MEETING_RELATION_WORDS[l.relation] || 'Linked to'} “${
        l.meeting_subject || 'meeting'}”${meetingDay(l) ? ` on ${meetingDay(l)}` : ''}`).join('\n');
    const tone = promise ? '#b91c1c' : prep ? '#2563eb' : null;
    return (
        <Tip title={<span style={{ whiteSpace: 'pre-line' }}>{title}</span>}
            explain={X.meetingChip(link)}>
            <Chip label={label} size="small" variant={promise ? 'filled' : 'outlined'} sx={{
                height: 20, fontSize: 10.5, fontWeight: promise || prep ? 700 : 500, borderRadius: '4px',
                color: tone || 'var(--color-text-secondary)',
                bgcolor: promise ? 'rgba(185,28,28,.14)' : 'transparent',
                borderColor: tone || 'var(--color-border, rgba(127,127,127,.3))',
            }} />
        </Tip>
    );
}

/** Group-by dimension: which meeting work came out of. */
export const meetingDimension = (linksByItem) => ({
    key: 'meeting',
    label: 'Meeting',
    of: (i) => {
        const links = linksByItem[`${i.source}:${i.id}`];
        if (!links || !links.length) return 'Not linked to a meeting';
        const l = links.find((x) => x.relation === 'promised_at') || links[0];
        return l.meeting_subject || 'Meeting';
    },
});

// ---------------------------------------------------------------------------
// People on an item — owner, requestor, stakeholders — one rendering
// ---------------------------------------------------------------------------

const short = (s) => String(s || '').split('@')[0];

/**
 * The three people fields, rendered identically on every surface.
 *
 * An empty owner is said out loud ("Unassigned", amber) rather than left as
 * a gap: an item with no owner is one the cadence job never nudges, and 152
 * of 247 open items were in that state when this was written. Requestor and
 * stakeholders are shown when set and collapsed to a count when not, so a
 * dense row stays one line.
 */
export function WorkPeople({ item, dense = false }) {
    const nameOf = usePeopleNames();
    const owner = item.owner;
    // A definite "the nudge cannot reach this owner" (ownerReach.js); null
    // while unknown or if the check failed, which shows no flag at all.
    const reach = useOwnerReach(item);
    const unreachable = !!owner && reach && reach.reachable === false;
    const stake = item.stakeholders || [];
    // A name where one is known; in a dense row an unknown email shortens to
    // its local part so the line still fits.
    const show = (v) => { const n = nameOf(v); return n !== v ? n : (dense ? short(v) : v); };
    const both = (v) => { const n = nameOf(v); return n !== v ? `${n} (${v})` : v; };
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75,
            minWidth: 0, flexWrap: dense ? 'nowrap' : 'wrap' }}>
            <Tip explain={X.owner(item)}
                title={!owner ? 'No owner — the update cadence will never nudge this item'
                    : unreachable ? `Owner: ${both(owner)} — nudges can't reach them: ${reach.reason}`
                        : `Owner: ${both(owner)}`}>
                <Typography component="span" data-testid={unreachable ? 'owner-unreachable' : undefined}
                    sx={{ fontSize: 11, whiteSpace: 'nowrap',
                        color: owner && !unreachable ? 'var(--color-text-secondary)' : '#b45309',
                        fontStyle: owner ? 'normal' : 'italic', fontWeight: owner ? 400 : 600,
                        textDecoration: unreachable ? 'underline dotted' : 'none' }}>
                    {owner ? show(owner) : 'Unassigned'}
                </Typography>
            </Tip>
            <Tip explain={X.requestor(item)}
                title={item.peopleUnavailable ? "Couldn't read the requestor store; nothing is lost, it just could not be read"
                    : item.requestor ? `Requested by ${both(item.requestor)}` : 'No requestor recorded'}>
                <Typography component="span" sx={{ fontSize: 11, whiteSpace: 'nowrap',
                    color: item.peopleUnavailable ? '#b45309' : 'var(--color-text-secondary)',
                    opacity: item.requestor || item.peopleUnavailable ? 1 : 0.55 }}>
                    {item.peopleUnavailable ? '· req couldn’t read' : item.requestor ? `· req ${show(item.requestor)}` : '· req —'}
                </Typography>
            </Tip>
            {item.source === 'todo' && item.checkinWith && (
                <Tip explain={X.checkinWith}
                    title={`Check in with ${item.checkinWith}`}>
                    <Typography component="span" sx={{ fontSize: 11, whiteSpace: 'nowrap',
                        color: 'var(--color-text-secondary)' }}>
                        {`· check in with ${dense ? item.checkinWith.replace('Nexus Implementations Team', 'Nexus Impl.') : item.checkinWith}`}
                    </Typography>
                </Tip>
            )}
            <Tip explain={X.stakeholders(item)}
                title={item.peopleUnavailable ? "Couldn't read the stakeholder store"
                    : stake.length ? `Stakeholders: ${stake.map(both).join(', ')}` : 'No stakeholders — nobody is escalated to if this stalls'}>
                <Typography component="span" sx={{ fontSize: 11, whiteSpace: 'nowrap',
                    color: item.peopleUnavailable ? '#b45309' : 'var(--color-text-secondary)',
                    opacity: stake.length || item.peopleUnavailable ? 1 : 0.55 }}>
                    {item.peopleUnavailable ? '· stakeholders couldn’t read'
                        : `· ${stake.length} stakeholder${stake.length === 1 ? '' : 's'}`}
                </Typography>
            </Tip>
            <ClientOwnerTag item={item} />
        </Box>
    );
}

/**
 * "· district: <name>" (#4320): the person at the district working this item,
 * after the people Nexus chases. Added information only — shown when one is
 * set, "couldn't read" when the store is unreadable, nothing while loading.
 * Never nudged or emailed; the hover says so.
 */
export function ClientOwnerTag({ item }) {
    const { status, value } = useClientOwner(item);
    if (status === 'error') {
        return (
            <Tip explain={X.clientOwner} title="Couldn't read the client owner store; nothing is lost, it just could not be read">
                <Typography component="span" sx={{ fontSize: 11, whiteSpace: 'nowrap', color: '#b45309' }}>
                    · district couldn’t read
                </Typography>
            </Tip>
        );
    }
    // An email thread names its district person itself (the first district
    // sender); a saved client owner, if any, outranks it.
    const shown = (status === 'ready' && value) || item.clientOwnerDerived || '';
    if (!shown || (status !== 'ready' && !item.clientOwnerDerived)) return null;
    const derived = !(status === 'ready' && value);
    return (
        <Tip explain={X.clientOwner}
            title={`Client owner (district): ${shown}${derived ? ' — from the thread (first district sender)' : ''} — for the team's information; Nexus never emails or nudges them`}>
            <Typography component="span" data-testid="client-owner" sx={{ fontSize: 11, whiteSpace: 'nowrap',
                color: 'var(--color-text-secondary)' }}>
                {`· district: ${personName(shown) || shown}`}
            </Typography>
        </Tip>
    );
}

/**
 * The client(s) an item belongs to, as the row shows them: one name, or the
 * first and a count ("Hutto +2") when it is attributed to several. Ambiguous
 * items name their candidates, the same list the "may not be this client" chip
 * reads from.
 */
export const clientDisplay = (item) => {
    const ids = item.clientIds || [];
    const names = item.attribution === 'ambiguous' && (item.candidateTitles || []).length
        ? item.candidateTitles
        : [item.clientTitle || item.clientLabel].filter(Boolean);
    const count = Math.max(ids.length, names.length);
    if (!count) return { label: 'No client', names: [], count: 0, linkId: null };
    const first = names[0] || 'Unnamed client';
    return {
        label: count > 1 ? `${first} +${count - 1}` : first,
        names,
        count,
        // One client = a link to it; several = no single place to go.
        linkId: ids.length === 1 ? ids[0] : null,
    };
};

/**
 * The client name on every work row (#4320), single-client pages included.
 * A link to the client's Client Overview when there is exactly one. The row
 * itself is a link, so this is a role="link" span (no nested anchor) that
 * navigates without opening the item.
 */
export function ClientName({ item }) {
    const navigate = useNavigate();
    const d = clientDisplay(item);
    const go = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/cs?client=${encodeURIComponent(d.linkId)}`);
    };
    const title = !d.count ? 'Not attributed to any client'
        : d.count > 1 ? `Clients: ${d.names.join(', ') || `${d.count} clients`}`
            : `Client: ${d.label} — open its Client Overview`;
    return (
        <Tip explain={X.clientName(item)} title={title}>
            <Typography component="span" data-testid="row-client"
                {...(d.linkId ? {
                    role: 'link', tabIndex: 0, onClick: go,
                    onKeyDown: (e) => { if (e.key === 'Enter') go(e); },
                } : {})}
                sx={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
                    color: d.count ? 'var(--color-text-primary, inherit)' : 'var(--color-text-secondary)',
                    fontStyle: d.count ? 'normal' : 'italic',
                    cursor: d.linkId ? 'pointer' : 'default',
                    '&:hover': d.linkId ? { textDecoration: 'underline', color: BLUE } : {} }}>
                {d.label}
            </Typography>
        </Tip>
    );
}

const SIGNAL_TAGS = [
    { kind: 'access_granted', label: 'Access granted', fg: '#047857', bg: 'rgba(4,120,87,.12)' },
    { kind: 'credentials_link', label: 'Credentials shared', fg: '#0038b8', bg: 'rgba(0,56,184,.10)' },
    { kind: 'files_sent', label: 'Data ready', fg: '#6d28d9', bg: 'rgba(109,40,217,.10)' },
];

export const agoShort = (iso, now = new Date()) => {
    const d = Math.max(0, Math.floor((now - new Date(iso)) / 86400000));
    return d === 0 ? 'today' : `${d}d ago`;
};

/**
 * Access / credentials / data tags on DevOps rows — only for Patrick, Eric and
 * Walker (services/accessSignals.js). Added AFTER the frozen chips, never among
 * them. Each is the newest signal of that kind from implementations@ or ops@,
 * matched to this ticket by the model; the tooltip says so and links to Ready
 * for me. If the signals couldn't be read, the row says that once instead of
 * looking like "no signal".
 */
export function AccessSignalTags({ item, now }) {
    const { status, signals } = useAccessSignals();
    if (status === 'off' || status === 'loading' || item.source !== 'ado') return null;
    if (status === 'error') {
        return (
            <Tip title="Access signals couldn't be read, so this row can't say whether access arrived." explain={X.accessSignalsUnread}>
                <Chip label="access: couldn’t read" size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 600, color: '#b45309', bgcolor: 'rgba(180,83,9,.12)', borderRadius: '4px' }} />
            </Tip>
        );
    }
    const mine = signals[String(item.id)];
    if (!mine) return null;
    return SIGNAL_TAGS.filter((t) => mine[t.kind]).map((t) => {
        const s = mine[t.kind];
        return (
            <Tip key={t.kind} explain={X.accessSignal(t.label)}
                title={`${s.district ? `${s.district}: ` : ''}${s.summary || t.label} — from an email to implementations@ or ops@; `
                    + `matched to this ticket by the model (${s.confidence}), so check it. Details on Ready for me.`}>
                <Chip component={RouterLink} to="/work/ready" clickable size="small"
                    label={`${t.label} ${agoShort(s.received, now)}`}
                    sx={{ height: 20, fontSize: 10, fontWeight: 600, color: t.fg, bgcolor: t.bg, borderRadius: '4px' }} />
            </Tip>
        );
    });
}

/**
 * Which DevOps area a DevOps item belongs to (ADO #4230): the last segment of
 * its System.AreaPath, e.g. "Super Portal", "Integrations System", "Product".
 * Read off the item, so a new area shows up with no code change. Monday,
 * to-do and request rows render nothing here. A DevOps item that arrived
 * without an area says so rather than looking area-less.
 */
export function areaLeaf(areaPath) {
    const parts = String(areaPath || '').split('\\').map((p) => p.trim()).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
}

export function AreaChip({ item }) {
    if (item.source !== 'ado') return null;
    const leaf = areaLeaf(item.area);
    if (!leaf) {
        return (
            <Tip title="This item's DevOps area couldn't be read." explain={X.areaChip(item)}>
                <Chip label="area: couldn’t read" size="small" data-testid="area-chip"
                    sx={{ height: 20, fontSize: 10, fontWeight: 600, color: '#b45309',
                        bgcolor: 'rgba(180,83,9,.12)', borderRadius: '4px', flexShrink: 0 }} />
            </Tip>
        );
    }
    return (
        <Tip title={`DevOps area: ${item.area}`} explain={X.areaChip(item)}>
            <Chip label={leaf} size="small" variant="outlined" data-testid="area-chip"
                sx={{ height: 20, fontSize: 10, fontWeight: 600, color: 'var(--color-text-secondary)',
                    borderColor: 'var(--color-border, rgba(127,127,127,.35))', borderRadius: '4px', flexShrink: 0 }} />
        </Tip>
    );
}

/**
 * THE work item row. Every list of work — the explorer's leaves, a client's
 * Work tab, the work under a meeting, the far end of a link — renders this,
 * so the chips, the people and the actions are the same everywhere.
 *
 *   [leading] ● Title ……… [status][meeting][blocked][due][priority][flag][access tags*]  Client · owner · req · N stakeholders [· district: X]  date ↗ [actions]
 *   (* Patrick, Eric and Walker only: AccessSignalTags)
 *   Client and "· district" were added 2026-10-12 (#4320) as information, before
 *   and after the people; nothing that was there moved.
 *
 * `dense` keeps it to one line with truncation (explorer, meetings); the
 * default wraps the chips and people under the title (client tab). `actions`
 * is the WorkActions element, passed in rather than imported to avoid the
 * cycle WorkActions → workUi → WorkActions.
 */
export function WorkItemRow({ item, mode, meetingLinks, actions, leading = null, trailing = null,
    dense = false, sx = {} }) {
    const age = daysSince(item.createdOn);
    const chips = (
        <>
            <StatusChip item={item} />
            <MeetingChip links={(meetingLinks || {})[`${item.source}:${item.id}`]} />
            <BlockedChip item={item} days={daysSince(item.movedOn)} />
            <DueChip item={item} />
            <PriorityChip value={item.priority} />
            {item.attribution === 'ambiguous' && (
                <Tip title={`Logged against "${item.clientLabel}", which matches `
                    + `${(item.candidateTitles || []).join(' and ')}. Confirm before counting it as this district's work.`}
                    explain={X.ambiguousChip(item)}>
                    <Chip label="may not be this client" size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 600, color: '#b45309',
                            bgcolor: 'rgba(180,83,9,.14)', borderRadius: '4px' }} />
                </Tip>
            )}
            <AccessSignalTags item={item} />
            <AreaChip item={item} />
        </>
    );
    const opened = (
        <Tip explain={X.openedOn(item)}
            title={`Raised ${fmtDate(item.createdOn)}${item.creator ? ` by ${personName(item.creator)}` : ''}`}>
            <Typography component="span" sx={{ fontSize: 11, color: 'var(--color-text-secondary)',
                whiteSpace: 'nowrap', flexShrink: 0 }}>
                {dense ? fmtDate(item.createdOn)
                    : `opened ${fmtDate(item.createdOn)}${item.creator ? ` by ${personName(item.creator)}` : ''}${
                        age !== null && age > 30 ? ` (${age}d ago)` : ''}`}
            </Typography>
        </Tip>
    );
    const openIcon = item.url && (
        <Tip explain={X.openIcon} title="">
            <OpenInNewIcon sx={{ fontSize: 13, color: 'var(--color-text-secondary)', flexShrink: 0 }} />
        </Tip>
    );

    if (dense) {
        // Two lines, not one: with six actions, five chips and three people
        // fields on a single line the title was squeezed to "E…" at 1064px.
        // The title keeps the first line with the chips and the actions; the
        // people and the date sit under it, indented to the title.
        return (
            <WorkItemLink item={item} sx={{
                py: 0.6, flexWrap: 'wrap', rowGap: 0.15,
                borderBottom: '1px solid var(--color-border, rgba(127,127,127,0.15))',
                '&:hover': { background: 'rgba(127,127,127,0.06)' }, ...sx,
            }}>
                {leading}
                <SourceDot source={item.source} mode={mode} />
                <Explain text={X.title(item)} sx={{ flex: '1 1 160px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                    </Typography>
                </Explain>
                {chips}
                {openIcon}
                {actions}
                {trailing}
                <Box sx={{ flexBasis: '100%', pl: leading ? 0 : 2.1, display: 'flex',
                    alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                    <ClientName item={item} />
                    <Typography component="span" sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>·</Typography>
                    <WorkPeople item={item} dense />
                    <Typography component="span" sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>·</Typography>
                    {opened}
                </Box>
            </WorkItemLink>
        );
    }

    return (
        <WorkItemLink item={item} sx={{
            alignItems: 'flex-start', gap: 1.5, py: 0.75,
            borderBottom: '1px solid var(--color-border, rgba(128,128,128,.15))',
            '&:hover': { background: 'rgba(127,127,127,0.06)' }, ...sx,
        }}>
            {leading}
            <Box sx={{ mt: 0.55 }}><SourceDot source={item.source} mode={mode} /></Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Explain text={X.title(item)} block>
                    <Typography sx={{ fontSize: 13, lineHeight: 1.45 }}>{item.title}</Typography>
                </Explain>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4, flexWrap: 'wrap' }}>
                    {chips}
                    {item.type && (
                        <Tip explain={X.typeLabel} title="">
                            <Typography sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                                {item.type}
                            </Typography>
                        </Tip>
                    )}
                    <ClientName item={item} />
                    <WorkPeople item={item} />
                    <Typography component="span" sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>·</Typography>
                    {opened}
                </Box>
            </Box>
            {openIcon && <Box sx={{ mt: 0.3 }}>{openIcon}</Box>}
            <Box sx={{ mt: 0.1, display: 'inline-flex', alignItems: 'center' }}>{actions}{trailing}</Box>
        </WorkItemLink>
    );
}
